import { useEffect, useState } from "react";
import { Plus, Minus, Pencil, Search, AlertTriangle, Boxes } from "lucide-react";
import { toast } from "sonner";
import api, { money, fileUrl, formatApiError } from "@/lib/api";
import { UNITS } from "@/lib/constants";
import { useApp } from "@/context/AppContext";
import { PageHeader, ConfirmDelete, Field, PhotoUpload } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const blank = () => ({ name: "", sku: "", category: "", quantity: 0, unit: "pieza", unit_cost: 0, min_stock: 0, photo_path: "", notes: "" });

function MaterialDialog({ item, onClose, onSaved }) {
  const [f, setF] = useState(item);
  useEffect(() => { setF(item); }, [item]);
  if (!f) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async (e) => {
    e.preventDefault();
    const body = { ...f, quantity: Number(f.quantity || 0), unit_cost: Number(f.unit_cost || 0), min_stock: Number(f.min_stock || 0) };
    try {
      if (f.id) await api.put(`/materials/${f.id}`, body); else await api.post("/materials", body);
      toast.success("Material guardado");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{f.id ? "Editar material" : "Nuevo material"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-4 sm:grid-cols-2" data-testid="material-form">
          <div className="sm:col-span-2"><PhotoUpload value={f.photo_path} onChange={set("photo_path")} testId="material-photo" /></div>
          <Field label="Nombre *" className="sm:col-span-2"><Input required value={f.name} onChange={set("name")} placeholder="Ej. MDF 15 mm" data-testid="material-name-input" /></Field>
          <Field label="Código / SKU"><Input value={f.sku} onChange={set("sku")} data-testid="material-sku-input" /></Field>
          <Field label="Categoría"><Input value={f.category} onChange={set("category")} placeholder="Tableros, madera, herrajes…" data-testid="material-category-input" /></Field>
          <Field label="Cantidad"><Input type="number" step="any" min="0" value={f.quantity} onChange={set("quantity")} data-testid="material-quantity-input" /></Field>
          <Field label="Unidad">
            <Select value={f.unit} onValueChange={set("unit")}>
              <SelectTrigger data-testid="material-unit-select"><SelectValue /></SelectTrigger>
              <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Costo unitario"><Input type="number" step="any" min="0" value={f.unit_cost} onChange={set("unit_cost")} data-testid="material-cost-input" /></Field>
          <Field label="Stock mínimo (alerta)"><Input type="number" step="any" min="0" value={f.min_stock} onChange={set("min_stock")} data-testid="material-min-input" /></Field>
          <Field label="Notas" className="sm:col-span-2"><Textarea rows={2} value={f.notes} onChange={set("notes")} data-testid="material-notes-input" /></Field>
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" className="bg-amber-700 hover:bg-amber-800" data-testid="material-save-button">Guardar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MaterialCard({ m, sym, onEdit, onAdjust, onDelete }) {
  const low = m.min_stock > 0 && m.quantity <= m.min_stock;
  return (
    <div className={`rounded-xl border bg-white overflow-hidden fade-up ${low ? "border-red-300" : ""}`} data-testid={`material-card-${m.id}`}>
      <div className="relative aspect-[4/3] bg-stone-100">
        {m.photo_path ? <img src={fileUrl(m.photo_path)} alt={m.name} className="h-full w-full object-cover" /> :
          <div className="h-full grid place-items-center text-stone-300"><Boxes className="h-10 w-10" /></div>}
        {low && <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-red-600 text-white text-[11px] font-semibold px-2 py-0.5"><AlertTriangle className="h-3 w-3" />Stock bajo</span>}
        {m.category && <span className="absolute top-2 right-2 rounded-full bg-white/95 text-[11px] font-semibold px-2 py-0.5">{m.category}</span>}
      </div>
      <div className="p-4">
        <p className="font-bold leading-tight">{m.name}</p>
        <p className="text-xs text-stone-500 font-mono">{m.sku || "—"} · {money(m.unit_cost, sym)}/{m.unit}</p>
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => onAdjust(m, -Math.min(1, m.quantity))} disabled={m.quantity <= 0} data-testid={`material-minus-${m.id}`}><Minus className="h-3 w-3" /></Button>
            <span className={`min-w-[64px] text-center font-mono font-semibold ${low ? "text-red-700" : ""}`} data-testid={`material-qty-${m.id}`}>{m.quantity} <span className="text-[10px] text-stone-500">{m.unit}</span></span>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => onAdjust(m, 1)} data-testid={`material-plus-${m.id}`}><Plus className="h-3 w-3" /></Button>
          </div>
          <div className="flex">
            <Button variant="ghost" size="icon" onClick={() => onEdit(m)} data-testid={`material-edit-${m.id}`}><Pencil className="h-4 w-4" /></Button>
            <ConfirmDelete onConfirm={() => onDelete(m)} testId={`material-delete-${m.id}`} label="este material" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Materials() {
  const { settings } = useApp();
  const sym = settings?.currency_symbol || "$";
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [onlyLow, setOnlyLow] = useState(false);

  const load = () => api.get("/materials").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const onAdjust = async (m, delta) => {
    try {
      const { data } = await api.post(`/materials/${m.id}/adjust`, { delta });
      setItems((list) => list.map((x) => (x.id === m.id ? data : x)));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const onDelete = async (m) => { await api.delete(`/materials/${m.id}`); toast.success("Material eliminado"); load(); };

  const low = (m) => m.min_stock > 0 && m.quantity <= m.min_stock;
  const shown = items.filter((m) => (!onlyLow || low(m)) && `${m.name} ${m.sku} ${m.category}`.toLowerCase().includes(q.toLowerCase()));
  const value = items.reduce((a, m) => a + m.quantity * m.unit_cost, 0);

  return (
    <div>
      <PageHeader title="Stock de materiales" subtitle={`${items.length} materiales · Valor en inventario ${money(value, sym)}`}>
        <Button className="bg-amber-700 hover:bg-amber-800" onClick={() => setEditing(blank())} data-testid="new-material-button"><Plus className="h-4 w-4 mr-1" />Nuevo material</Button>
      </PageHeader>
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-stone-400" />
          <Input className="pl-9 bg-white" placeholder="Buscar material, código o categoría" value={q} onChange={(e) => setQ(e.target.value)} data-testid="material-search-input" />
        </div>
        <Button variant={onlyLow ? "default" : "outline"} onClick={() => setOnlyLow(!onlyLow)} className={onlyLow ? "bg-red-600 hover:bg-red-700" : ""} data-testid="material-low-filter">
          <AlertTriangle className="h-4 w-4 mr-1" />Solo stock bajo ({items.filter(low).length})
        </Button>
      </div>
      {shown.length === 0 && <p className="text-center text-stone-400 py-16">No hay materiales para mostrar</p>}
      <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
        {shown.map((m) => <MaterialCard key={m.id} m={m} sym={sym} onEdit={setEditing} onAdjust={onAdjust} onDelete={onDelete} />)}
      </div>
      <MaterialDialog item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}
